const _excluded = ["selectedLegalTerms", "legalTerms", "showViewTosButton", "onChange", "onRemove", "onUpload"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useEffect, useRef, useState } from 'react';
import { useGlobalContext } from 'seagull';
import { useIoContext, useRelayContext, useStore, useStoreApi } from "../../contexts";
import { IconButton, MenuItem, Spinner, TextButton, TextField, Typography } from 'seagull';
import { Plus, TrashFill18 } from 'seagull/icons';
import { sendErrReport } from "../../infra/utils";
import createLegalTermsDocument from "../../mutations/createLegalTermsDocumentMutation";
import { getPdfViewUrl, uploadPdf } from "../../queries/uploadPDF";
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import RichTextViewerModal from "../RichTextViewerModal";
import styles from "./LegalTermsDropdown.module.scss";
import { legalTermsModel } from "../../models";
const loadingValue = 'loading';

const removePDFsuffix = filename => filename.replace(/\.pdf$/i, '');

const makeMapFromLegalTermsArray = docs => new Map(docs.slice().sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())).map(doc => [doc.id, doc]));

export default function LegalTermsDropdown(_ref) {
  var _props$dataTestid;

  let {
    selectedLegalTerms,
    legalTerms,
    showViewTosButton = false,
    onChange: _onChange,
    onRemove,
    onUpload
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  const globalContext = useGlobalContext();
  const {
    environment
  } = useRelayContext();
  const {
    openNewTab
  } = useIoContext();
  const [showModal, setShowModal] = useState(false);
  const [internalSelectedLegalTerms, setInternalSelectedLegalTerms] = useState(selectedLegalTerms === null || selectedLegalTerms === void 0 ? void 0 : selectedLegalTerms.id);
  const [internalLegalTerms, setInternalLegalTerms] = useState(() => makeMapFromLegalTermsArray(legalTerms));
  const [isLoading, setIsLoading] = useState(true);
  const pdfSizeLimit = useStore(state => state.appConfig.termsAndConditionsPDFSizeLimitInMegabytes);
  const {
    getState
  } = useStoreApi();
  const fileInputRef = useRef(null);
  useEffect(() => {
    setIsLoading(!selectedLegalTerms || internalLegalTerms.size === 0);
  }, [selectedLegalTerms, internalLegalTerms]);
  useEffect(() => {
    setInternalLegalTerms(makeMapFromLegalTermsArray(legalTerms));
  }, [legalTerms]);
  useEffect(() => {
    setInternalSelectedLegalTerms(selectedLegalTerms === null || selectedLegalTerms === void 0 ? void 0 : selectedLegalTerms.id);
  }, [selectedLegalTerms]);
  const testPrefix = (_props$dataTestid = props['data-testid']) !== null && _props$dataTestid !== void 0 ? _props$dataTestid : 'legal-terms-uploader';

  const handlePDFUpload = async e => {
    e.preventDefault();

    try {
      const file = e.target.files && e.target.files[0];

      if (!file) {
        throw new PDFError('An uknown error has occurred.', 'unknown');
      }

      if (file.type !== 'application/pdf') {
        throw new PDFError('File is not a valid PDF.', 'invalid-pdf');
      }

      if (file.size > pdfSizeLimit * 1024 * 1024) {
        throw new PDFError('Selected file is too large. Please select a smaller one.', 'too-large');
      }

      setIsLoading(true);
      const pdfUniqueName = await uploadPdf(environment, file);
      const legalTermsDoc = legalTermsModel.makePdfLegalTerms({
        name: file.name,
        pdfUniqueName
      }, getState().appConfig.generateUniqueId || generateUniqueId);
      await createLegalTermsDocument(environment, {
        id: legalTermsDoc.id,
        content: {
          type: 'pdf',
          pdfUniqueName: legalTermsDoc.pdfUniqueName,
          displayName: legalTermsDoc.name
        }
      });
      onUpload();
      setInternalLegalTerms(makeMapFromLegalTermsArray([...internalLegalTerms.values(), legalTermsDoc]));
      setInternalSelectedLegalTerms(legalTermsDoc.id);

      _onChange(legalTermsDoc.id);
    } catch (error) {
      const message = error instanceof PDFError ? error.message : 'An unknown error has occurred.';
      const id = error instanceof PDFError ? error.id : 'unknown';
      globalContext.showErrorToast(message, "".concat(id, "-error-toast"));
      sendErrReport(error);
      setInternalSelectedLegalTerms(selectedLegalTerms === null || selectedLegalTerms === void 0 ? void 0 : selectedLegalTerms.id);
    } finally {
      setIsLoading(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles.root,
    "data-testid": testPrefix
  }, /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".pdf",
    onChange: handlePDFUpload,
    "data-testid": testPrefix + '-dropdown-file-input',
    hidden: true
  }), /*#__PURE__*/React.createElement(TextField, _extends({}, props, {
    select: true,
    value: isLoading ? loadingValue : internalSelectedLegalTerms,
    onChange: _ref2 => {
      let {
        target: {
          value
        }
      } = _ref2;
      const newValue = internalLegalTerms.get(value);
      if (!newValue || newValue.id === 'upload-new') return;
      setInternalSelectedLegalTerms(newValue.id);

      _onChange(newValue.id);
    },
    "data-testid": testPrefix + '-dropdown',
    disabled: isLoading,
    SelectProps: {
      renderValue: value => {
        if (value === loadingValue) {
          return /*#__PURE__*/React.createElement(Typography, {
            className: styles.loadingRow,
            skin: "secondary"
          }, /*#__PURE__*/React.createElement(Spinner, {
            className: styles.spinner,
            size: 24,
            "data-testid": testPrefix + '-dropdown-spinner'
          }), ' ', "Loading\u2026");
        }

        const legalTerm = internalLegalTerms.get(value);
        return legalTerm ? removePDFsuffix(legalTerm.name) : '';
      }
    }
  }), isLoading && /*#__PURE__*/React.createElement(MenuItem, {
    value: loadingValue
  }), /*#__PURE__*/React.createElement(MenuItem, {
    value: "upload-new",
    "data-testid": testPrefix + '-dropdown-option-upload-new',
    className: styles.uploadPdfRow,
    onClick: () => {
      var _fileInputRef$current;

      return fileInputRef === null || fileInputRef === void 0 ? void 0 : (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click();
    }
  }, /*#__PURE__*/React.createElement(TextButton, {
    as: "p",
    underline: "none",
    prefixIcon: /*#__PURE__*/React.createElement(Plus, null)
  }, "Upload", ' ', /*#__PURE__*/React.createElement(Typography, {
    as: "span",
    skin: "secondary",
    variant: "paragraph2"
  }, "(PDF smaller than 50MB)"))), Array.from(internalLegalTerms.values()).map((_ref3, idx) => {
    let {
      id,
      name
    } = _ref3;
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: id,
      value: id,
      "data-testid": testPrefix + '-dropdown-option-' + idx
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.menuItem
    }, removePDFsuffix(name), onRemove && internalLegalTerms.size > 1 && /*#__PURE__*/React.createElement(IconButton, {
      noPadding: true,
      size: "small",
      skin: "error",
      "data-testid": "".concat(testPrefix, "-dropdown-option-").concat(idx, "-rm"),
      onClick: e => {
        e.stopPropagation();
        onRemove(id);
      }
    }, /*#__PURE__*/React.createElement(TrashFill18, null))));
  })), showViewTosButton && /*#__PURE__*/React.createElement("div", {
    className: styles.viewTosButton
  }, /*#__PURE__*/React.createElement(TextButton, {
    type: "button",
    "data-testid": testPrefix + '-view-tos-button',
    onClick: async () => {
      if (internalSelectedLegalTerms) {
        const selectedLegalTerms = internalLegalTerms.get(internalSelectedLegalTerms);

        if (selectedLegalTerms !== null && selectedLegalTerms !== void 0 && selectedLegalTerms.pdfUniqueName) {
          const url = await getPdfViewUrl(environment, undefined, selectedLegalTerms.pdfUniqueName, selectedLegalTerms.name);

          if (url) {
            openNewTab(url);
          }
        } else {
          setShowModal(true);
        }
      }
    }
  }, "View terms & conditions"), showModal && selectedLegalTerms && /*#__PURE__*/React.createElement(RichTextViewerModal, {
    show: true,
    title: removePDFsuffix(selectedLegalTerms.name),
    text: selectedLegalTerms.richTextContent,
    onCancel: () => setShowModal(false)
  })));
}

class PDFError extends Error {
  constructor(message, id) {
    super(message);
    this.id = id;
  }

}